import { useState } from 'react'
import { X, Minus, Plus, Calendar, Clock3 } from 'lucide-react'
import { formatRupiah } from '@/lib/utils'
import type { Produk } from '@/types'

interface QuickOrderModalProps {
  product: Produk
  isOpen: boolean
  onClose: () => void
  onSubmit: (qty: number, jumlah_hari?: number, tanggal_mulai?: string) => void
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function QuickOrderModal({ product, isOpen, onClose, onSubmit }: QuickOrderModalProps) {
  const today = new Date().toISOString().split('T')[0]

  const [qty, setQty] = useState(1)
  const [jumlah_hari, setJumlahHari] = useState(1)
  const [tanggal_mulai, setTanggalMulai] = useState(today)

  if (!isOpen) return null

  const isSewa = product.tipe === 'sewa'
  const hargaEfektif = product.harga
  const subtotal = isSewa
    ? hargaEfektif * qty * jumlah_hari
    : hargaEfektif * qty

  function handleSubmit() {
    if (isSewa) {
      onSubmit(qty, jumlah_hari, tanggal_mulai)
    } else {
      onSubmit(qty)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center md:justify-center">
      <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-6 animate-in slide-in-from-bottom-5 md:slide-in-from-bottom-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-900">
            {isSewa ? 'Sewa' : 'Pesan'} {product.nama}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Product Preview */}
        <div className="flex gap-3 mb-5 p-3 bg-gray-50 rounded-xl">
          <div className="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center text-2xl shrink-0 overflow-hidden">
            {product.gambar_url ? (
              <img src={product.gambar_url} alt={product.nama} className="w-full h-full object-cover rounded-lg" />
            ) : (
              '🎒'
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 text-sm">{product.nama}</p>
            <p className="text-blue-600 font-semibold text-sm">
              {formatRupiah(hargaEfektif)}{isSewa ? ' / hari' : ` / ${product.satuan}`}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Stok: {product.stok} {product.satuan}
            </p>
          </div>
        </div>

        {/* Rental: tanggal mulai + jumlah hari */}
        {isSewa && (
          <div className="space-y-4 mb-5">
            <div>
              <label className="flex items-center gap-1.5 text-xs text-gray-500 font-medium mb-1.5">
                <Calendar size={13} />
                Tanggal Mulai Sewa
              </label>
              <input
                type="date"
                min={today}
                value={tanggal_mulai}
                onChange={e => setTanggalMulai(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs text-gray-500 font-medium mb-1.5">
                <Clock3 size={13} />
                Jumlah Hari
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setJumlahHari(h => Math.max(1, h - 1))}
                  disabled={jumlah_hari <= 1}
                  className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-40"
                >
                  <Minus size={16} />
                </button>
                <span className="flex-1 text-center font-semibold text-lg">{jumlah_hari}</span>
                <button
                  onClick={() => setJumlahHari(h => h + 1)}
                  className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
              {tanggal_mulai && (
                <p className="text-xs text-gray-400 mt-1.5 text-center">
                  Kembali: <span className="font-medium text-gray-600">{addDays(tanggal_mulai, jumlah_hari)}</span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Quantity */}
        <div className="mb-5">
          <p className="text-xs text-gray-400 mb-2">Jumlah</p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setQty(q => Math.max(1, q - 1))}
              disabled={qty <= 1}
              className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              <Minus size={16} />
            </button>
            <span className="flex-1 text-center font-semibold text-lg">{qty}</span>
            <button
              onClick={() => setQty(q => Math.min(product.stok, q + 1))}
              disabled={qty >= product.stok}
              className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Subtotal */}
        <div className="bg-blue-50 rounded-xl p-3 mb-5">
          {isSewa ? (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>{formatRupiah(hargaEfektif)} × {qty} × {jumlah_hari} hari</span>
              </div>
              <div className="flex justify-between items-center font-bold">
                <span className="text-gray-700">Total</span>
                <span className="text-blue-600">{formatRupiah(subtotal)}</span>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Subtotal</span>
              <span className="font-bold text-blue-600">{formatRupiah(subtotal)}</span>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-semibold text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSewa && !tanggal_mulai}
            className="flex-1 py-3 rounded-xl font-semibold text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSewa ? 'Tambah ke Sewa' : 'Tambah ke Pesanan'}
          </button>
        </div>
      </div>
    </div>
  )
}
